import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../app/rootReducer';
import { AppDispatch } from '../../../app/store';
import { saveDraft } from './notesDraftSlice';
export const AUTO_SAVE_DEBOUNCE_MS = 30_000;
export const AUTO_SAVE_DELTA_CHARS = 50;

interface UseAutoSaveDraftArgs {
  applicationId: string;
  content: string;
  mibCodes: string[];
  enabled: boolean;
}

export interface UseAutoSaveDraftResult {
  flushNow: () => Promise<void>;
}

const sameCodes = (a: string[], b: string[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const useAutoSaveDraft = ({
  applicationId,
  content,
  mibCodes,
  enabled,
}: UseAutoSaveDraftArgs): UseAutoSaveDraftResult => {
  const dispatch = useDispatch<AppDispatch>();
  const lastSavedSnapshot = useSelector(
    (state: RootState) => state.notesDraft.lastSavedSnapshot,
  );
  const lastSavedMibCodes = useSelector(
    (state: RootState) => state.notesDraft.lastSavedMibCodes,
  );
  const isSaving = useSelector((state: RootState) => state.notesDraft.isSaving);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef(content);
  const mibCodesRef = useRef(mibCodes);
  const snapshotRef = useRef(lastSavedSnapshot);
  const savedMibCodesRef = useRef(lastSavedMibCodes);
  const isSavingRef = useRef(isSaving);
  const applicationIdRef = useRef(applicationId);

  contentRef.current = content;
  mibCodesRef.current = mibCodes;
  snapshotRef.current = lastSavedSnapshot;
  savedMibCodesRef.current = lastSavedMibCodes;
  isSavingRef.current = isSaving;
  applicationIdRef.current = applicationId;

  const clearPending = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const triggerSave = useCallback(async () => {
    clearPending();
    if (isSavingRef.current) {
      return;
    }
    if (
      contentRef.current === snapshotRef.current &&
      sameCodes(mibCodesRef.current, savedMibCodesRef.current)
    ) {
      return;
    }
    // Don't save when content is empty and no prior draft exists — mibCodes
    // may differ only because of pre-population, not a user change.
    if (
      !contentRef.current.trim() &&
      !snapshotRef.current &&
      savedMibCodesRef.current.length === 0
    ) {
      return;
    }
    await dispatch(
      saveDraft(
        applicationIdRef.current,
        contentRef.current,
        mibCodesRef.current,
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!enabled) {
      clearPending();
      return;
    }
    const contentUnchanged = content === lastSavedSnapshot;
    const codesUnchanged = sameCodes(mibCodes, lastSavedMibCodes);
    if (contentUnchanged && codesUnchanged) {
      clearPending();
      return;
    }

    clearPending();
    const delta = Math.abs(content.length - lastSavedSnapshot.length);
    if (!codesUnchanged || delta >= AUTO_SAVE_DELTA_CHARS) {
      triggerSave();
      return;
    }

    timerRef.current = setTimeout(() => {
      triggerSave();
    }, AUTO_SAVE_DEBOUNCE_MS);
  }, [
    enabled,
    content,
    mibCodes,
    lastSavedSnapshot,
    lastSavedMibCodes,
    triggerSave,
  ]);

  useEffect(() => {
    return () => clearPending();
  }, []);

  useEffect(() => {
    clearPending();
  }, [applicationId]);

  return {
    flushNow: triggerSave,
  };
};
